import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ButtonContainer,
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import { editSchema } from '../../schemas';
import validationSchema from './validations';

const FORM_ID = 'MAINTENANCE_RULE_EDIT_FORM';
const PERMISSION_SP = 'RULE';

function RuleEdit() {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/rules/${pageId}`;

  const preProcessRequestData = data => {
    const { id, conditions, outcome, priority, isActive, ...other } = data;
    return {
      isActive: isActive === 'true',
      priority: priority ? parseInt(priority, 10) : 0,
      conditions: conditions ? JSON.parse(conditions) : {},
      outcome: outcome ? JSON.parse(outcome) : {},
      ...other,
    };
  };

  const preProcessFetchData = data => {
    const { conditions, outcome, ...other } = data;
    return {
      ...other,
      conditions: conditions ? JSON.stringify(conditions, null, 2) : '',
      outcome: outcome ? JSON.stringify(outcome, null, 2) : '',
    };
  };

  const fetchDefaultValues = useCallback(async () => {
    const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/rules/${pageId}`;
    try {
      const response = await get(getPageDataApiUrl);
      if (response?.ok) {
        return {
          data: preProcessFetchData(response?.data),
          errors: null,
        };
      }
      return {
        data: null,
        errors: response?.errors || null,
      };
    } catch (err) {
      return {
        data: null,
        errors: [{ message: err.message }],
      };
    }
  }, [get, getApiUrl, pageId]);

  const onSubmit = async data => {
    try {
      const requestData = preProcessRequestData(data);
      const response = await put(updateApiUrl, { ...requestData });
      if (response?.ok) {
        snackbar.showMessage(
          formatMessage({
            id: 'RULE_WAS_SAVED_SUCCESSFULLY',
            defaultMessage: 'Rule was saved successfully',
          }),
        );
        return {
          nextValues: preProcessFetchData(response?.data),
          errors: null,
        };
      }
      return {
        nextValues: data,
        errors: response?.errors,
      };
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'RULES',
        defaultMessage: 'Rules',
      }),
      icon: IconLibrary.Rules,
      onClick: () => {
        navigate('/rules');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_RULE',
        defaultMessage: 'Edit Rule',
      }),
      icon: IconLibrary.Rule,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_RULE',
        defaultMessage: 'Create Rule',
      }),
      Icon: IconLibrary.Rule,
      variant: 'outlined',
      onClick: () => navigate('/rules/create'),
      permissionName: 'CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES',
    },
    {
      label: formatMessage({
        id: 'LIST_RULES',
        defaultMessage: 'List Rules',
      }),
      Icon: IconLibrary.Rules,
      variant: 'outlined',
      onClick: () => navigate('/rules'),
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'EDIT_RULE',
          defaultMessage: 'Edit Rule',
        })}>
        <Box>
          <Form
            formId={FORM_ID}
            ref={formRef}
            permissionEntities={[PERMISSION_SP]}
            onSubmit={onSubmit}
            schema={editSchema}
            validationSchema={validationSchema(formatMessage)}
            fetchDefaultValues={fetchDefaultValues}>
            <ButtonContainer>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({
                  id: 'SAVE_RULE',
                  defaultMessage: 'Save Rule',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_SYSTEM_SETTINGS_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => formRef.current?.reset({})}>
                {formatMessage({
                  id: 'RESET',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </ButtonContainer>
          </Form>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(RuleEdit);
